import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { Money } from "@shared/types/roomOffer";
import OfferIcon from "./OfferIcon";
import { formatMoney, type OffersCopy } from "./offersCopy";
import type { OffersScreenStyle } from "./OffersScreen";

/**
 * **The one picker** (`POS-7`, `docs/room-offers.md` §2).
 *
 * Every guest flow on the television is the same interaction — *pick from a
 * list, then confirm* — and the list is the only thing that differs:
 *
 * | Kind | The list is | Mode |
 * |---|---|---|
 * | `order` | goods, priced | `quantity` |
 * | `booking` · slots | bookable hours | `one` |
 * | `booking` · asap | amenities, free | `many` |
 *
 * So there is one component, one selection model ({@link OfferSelection}) and
 * one confirm step. A generated time slot is a {@link PickerItem} at this
 * boundary — `{ id: "2026-08-20T19:00", label: "19:00" }` — so the picker never
 * learns what a time is, and nobody writes a second one for the next kind.
 *
 * ## Why the layout is ROWS rather than a column count
 *
 * A grid whose rows mean nothing cannot say "Thursday". Rows are the unit here
 * because all three flows want them to mean something different and all three
 * want the same d-pad model: a day of hours is a row with a heading, an amenity
 * grid is rows of four, and a priced list is rows of one. The focus grid is
 * therefore `rows.length × widest row`, which is what lets the owner run the
 * whole choose step — picker, party-size stepper and the action — off a single
 * `useFocusGrid`.
 *
 * ## Why the priced list is one item per row
 *
 * Because a stepper and a grid cannot share Left and Right. `quantity` mode
 * spends them on − and + for the focused line (OK adds one, so the common case
 * is a single press), which means the row can only hold one item; the amenity
 * picker has nothing to step, so it keeps the icon grid. Inventing a modal
 * "left moves unless the quantity is above zero" would be a keyboard model
 * nobody can predict in the dark with a remote.
 *
 * ## What it never does
 *
 * It does not hide anything. A full hour is greyed and still focusable, because
 * "19:00 is gone" is information a guest wants and a silently missing hour reads
 * as a bug (§4). Pressing it is refused with words, not with nothing.
 */

/**
 * One thing a guest can pick, already localized by the caller.
 *
 * Deliberately NOT `OfferItem`: an authored line and a generated hour are the
 * same thing HERE, which is the whole ruling. Anything the picker would have to
 * ask the offer about — what an hour is, whether an item was enabled — has been
 * answered before it arrives.
 */
export interface PickerItem {
  id: string;
  /** The big word: "Clubsmörgås", "19:00", "Rena handdukar". */
  label: string;
  /** Catalogue icon key (`@shared/offerIcons`). Hours carry none. */
  icon?: string;
  /** A `/foyer-assets/…` photograph, for the lines that earned one. */
  imageUrl?: string;
  /** Per-unit price. Absent means free — never zero. */
  price?: Money;
  /** The small line underneath: "2 platser kvar", "Fullbokat". */
  note?: string;
  /** Shown, greyed, focusable — and refused on OK. */
  disabled?: boolean;
}

/** A row of the picker, with the heading it means (a day, usually). */
export interface PickerRow {
  /** Printed above the row. Absent draws no heading line at all. */
  heading?: string;
  items: PickerItem[];
}

/**
 * What the guest has picked: item id → quantity.
 *
 * One model for all three modes, and the modes differ only in what may be in
 * it: `one` holds at most one key, `many` holds any number of 1s, `quantity`
 * holds counts. A zero is never stored — an item at zero is an item that was
 * not picked, and keeping `{ club: 0 }` around would put an empty line on the
 * confirm step and a `quantity: 0` on the wire.
 */
export type OfferSelection = Readonly<Record<string, number>>;

export type PickerMode = "one" | "many" | "quantity";

/** The most of any one line a guest may order from a remote. */
export const MAX_QUANTITY = 20;

/** Everything picked, as `[id, quantity]`, in the rows' own order. */
export function selectedLines(
  rows: PickerRow[],
  selection: OfferSelection
): Array<{ item: PickerItem; quantity: number }> {
  const out: Array<{ item: PickerItem; quantity: number }> = [];
  for (const row of rows) {
    for (const item of row.items) {
      const quantity = selection[item.id] ?? 0;
      if (quantity > 0) out.push({ item, quantity });
    }
  }
  return out;
}

/**
 * OK on an item.
 *
 * `one` replaces (an hour is not a quantity), `many` toggles (a second press on
 * towels must take them off the list — with five keys there is no other way to
 * undo), `quantity` adds one and stops at {@link MAX_QUANTITY} rather than
 * wrapping to zero, because a press that silently emptied a line the guest was
 * building is the worst possible reading of "add".
 *
 * A disabled item returns the selection UNCHANGED and the caller says why. It
 * is the caller's job because only it knows which refusal applies — a full hour
 * and an item that went out of stock are the same greyed tile here.
 */
export function applyPick(
  selection: OfferSelection,
  item: PickerItem,
  mode: PickerMode
): OfferSelection {
  if (item.disabled) return selection;
  if (mode === "one") return { [item.id]: 1 };
  const current = selection[item.id] ?? 0;
  if (mode === "many") {
    if (current > 0) {
      const next = { ...selection };
      delete next[item.id];
      return next;
    }
    return { ...selection, [item.id]: 1 };
  }
  return { ...selection, [item.id]: Math.min(MAX_QUANTITY, current + 1) };
}

/**
 * Left / Right on a focused line in `quantity` mode.
 *
 * Stepping down to zero REMOVES the key rather than storing a zero, so
 * "nothing picked" has exactly one representation everywhere downstream.
 */
export function applyStep(
  selection: OfferSelection,
  item: PickerItem,
  delta: number
): OfferSelection {
  if (item.disabled) return selection;
  const next = Math.max(
    0,
    Math.min(MAX_QUANTITY, (selection[item.id] ?? 0) + delta)
  );
  const out = { ...selection };
  if (next === 0) delete out[item.id];
  else out[item.id] = next;
  return out;
}

/**
 * The running total of a selection: an amount, FREE, or a refusal to guess.
 *
 * Three answers because there are three cases and collapsing any two of them
 * lies to a guest:
 *
 * - `null` means **free**, not zero. The amenity picker is a legitimate
 *   zero-revenue request, and a `0 kr` on it would put a zero into the revenue
 *   series that POS-11 could not tell from a comped order (§5).
 * - `"mixed"` means the lines are priced in two currencies. Adding kronor to
 *   euros is wrong and dropping the euros is worse — it would quote an
 *   UNDERSTATED amount on the confirm step and then have the server refuse the
 *   whole request with `price_currency_mixed` a second later. The caller shows
 *   that refusal instead, so both sides of the wire say the same thing.
 * - anything else is the amount, and the offer's own price is counted once,
 *   exactly as `draftFromOffer` counts it server-side.
 */
export function selectionTotal(
  rows: PickerRow[],
  selection: OfferSelection,
  base?: Money
): Money | null | "mixed" {
  const parts: Money[] = base ? [base] : [];
  for (const { item, quantity } of selectedLines(rows, selection)) {
    if (item.price) {
      parts.push({
        amount: item.price.amount * quantity,
        currency: item.price.currency,
      });
    }
  }
  if (parts.length === 0) return null;
  const currency = parts[0].currency;
  if (parts.some((p) => p.currency !== currency)) return "mixed";
  return {
    amount: parts.reduce((sum, p) => sum + p.amount, 0),
    currency,
  };
}

/** How many focus columns the rows need. At least one, so an empty picker is safe. */
export function pickerColumns(rows: PickerRow[]): number {
  return Math.max(1, ...rows.map((r) => r.items.length));
}

interface Props {
  rows: PickerRow[];
  selection: OfferSelection;
  mode: PickerMode;
  style: OffersScreenStyle;
  copy: OffersCopy;
  /** Grid focus, owned by the choose step so one grid drives the whole page. */
  focus: { r: number; c: number };
  /** Focus follows the pointer in the CMS preview; a remote never fires it. */
  onHover?: (r: number, c: number) => void;
  onPick?: (item: PickerItem) => void;
}

export default function OfferPicker({
  rows,
  selection,
  mode,
  style,
  copy,
  focus,
  onHover,
  onPick,
}: Props) {
  // The rows slide under a clipped window rather than living in a scroll
  // container: `scrollIntoView` fights the d-pad on a panel whose smooth scroll
  // lands a frame late, and a native scrollbar has no place on a television.
  // Same technique as the card rail one level up.
  //
  // MEASURED rather than computed from a row count, because the rows are not
  // one height: a day of hours carries a heading and a priced line does not,
  // and a guessed row height left the last row sliced in half on a 1080p panel
  // (seen on hardware-free Chromium at 1920×1080, 2026-08-14). A slice reads as
  // a rendering fault, and a tile a guest cannot fully see is one they will not
  // press.
  //
  // `offsetTop` is measured from the nearest POSITIONED ancestor, which is why
  // the content below carries `position: relative` — one word that makes these
  // numbers mean "inside the picker" rather than "inside whatever the template
  // happened to position". Without it the answer depends on the host: eight
  // templates wrap this screen differently, and Chromium would hand back an
  // offset that includes Atelier's header on one and not on another.
  //
  // (A transform also establishes the containing block, so this happens to work
  // today by accident of the `translateY` below. Depending on that would make a
  // future `transform: none` — an animation switched off, a reduced-motion
  // branch — silently push the first rows off the top, where no arrow can reach
  // them. jsdom reports 0 for every offset, so no test can hold this honest;
  // the declaration has to.)
  const viewportRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [shift, setShift] = useState(0);
  useEffect(() => {
    const viewport = viewportRef.current;
    const row = rowRefs.current[focus.r];
    if (!viewport || !row) return;
    const height = viewport.clientHeight;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    setShift((current) => {
      if (top - current < 0) return top;
      if (bottom - current > height) return Math.max(0, bottom - height);
      return current;
    });
  }, [focus.r, rows]);
  return (
    <div
      ref={viewportRef}
      data-testid="offer-picker"
      style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}
    >
      <div
        style={{
          // Load-bearing, not decoration — see the measurement above.
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: mode === "quantity" ? 10 : 18,
          transform: `translateY(${-shift}px)`,
          transition: "transform 220ms cubic-bezier(0.2,0.8,0.2,1)",
        }}
      >
        {rows.map((row, r) => (
          // Keyed on the row's first item, never on the heading: a day too
          // long for one row repeats with a BLANK heading, and two blank keys
          // would make React reuse the wrong row's DOM on the next render.
          <div
            key={row.items[0]?.id ?? r}
            ref={(el) => {
              rowRefs.current[r] = el;
            }}
          >
            {row.heading && (
              <div
                style={{
                  fontSize: 18,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: style.inkDim,
                  fontWeight: 700,
                  // Never below 1.2 next to `overflow: hidden` — see
                  // docs/memory/global/pitfalls/tv-tight-line-height-clipping.md
                  lineHeight: 1.3,
                  marginBottom: 8,
                }}
              >
                {row.heading}
              </div>
            )}
            <div style={{ display: "flex", gap: 14, flexWrap: "nowrap" }}>
              {row.items.map((item, c) => (
                <Tile
                  key={item.id}
                  item={item}
                  quantity={selection[item.id] ?? 0}
                  mode={mode}
                  style={style}
                  copy={copy}
                  // Clamped against THIS row: rows differ in length (a short
                  // last day, a half-full grid) and the focus grid is
                  // rectangular, so an un-clamped comparison would leave the
                  // ring nowhere at all on the short rows.
                  focused={
                    focus.r === r &&
                    Math.min(focus.c, row.items.length - 1) === c
                  }
                  onHover={() => onHover?.(r, c)}
                  onPick={() => onPick?.(item)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One pickable thing.
 *
 * A ring and a fill rather than a size change: tiles that grow on focus make the
 * whole picker jump on every arrow press, which on a ten-foot screen reads as
 * the page being redrawn.
 */
function Tile({
  item,
  quantity,
  mode,
  style,
  copy,
  focused,
  onHover,
  onPick,
}: {
  item: PickerItem;
  quantity: number;
  mode: PickerMode;
  style: OffersScreenStyle;
  copy: OffersCopy;
  focused: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const picked = quantity > 0;
  const wide = mode === "quantity";
  const frame: CSSProperties = {
    appearance: "none",
    display: "flex",
    alignItems: "center",
    gap: 16,
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    boxSizing: "border-box",
    padding: wide ? "14px 20px" : "16px 18px",
    width: wide ? "100%" : undefined,
    minWidth: wide ? undefined : 168,
    minHeight: wide ? 78 : 150,
    flexDirection: wide ? "row" : "column",
    justifyContent: wide ? "flex-start" : "center",
    borderRadius: style.radius,
    border: `1px solid ${focused || picked ? style.accent : style.rule}`,
    background: picked ? `${style.accent}22` : style.card,
    color: style.ink,
    // Greyed, not hidden. Still focusable, so a guest can read why.
    opacity: item.disabled ? 0.45 : 1,
    boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
  };
  return (
    <button
      type="button"
      data-testid={`picker-item-${item.id}`}
      aria-current={focused ? "true" : undefined}
      aria-disabled={item.disabled ? "true" : undefined}
      onMouseEnter={onHover}
      onClick={onPick}
      style={frame}
    >
      {item.imageUrl ? (
        <span
          aria-hidden
          style={{
            flex: "0 0 auto",
            width: wide ? 48 : 56,
            height: wide ? 48 : 56,
            borderRadius: 8,
            background: `url(${item.imageUrl}) center/cover`,
          }}
        />
      ) : (
        item.icon !== undefined && (
          <OfferIcon
            name={item.icon}
            size={wide ? 40 : 46}
            color={picked ? style.accent : style.ink}
          />
        )
      )}
      <span
        style={{
          flex: wide ? "1 1 auto" : "0 0 auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: wide ? "flex-start" : "center",
          textAlign: wide ? "left" : "center",
        }}
      >
        <span style={{ fontSize: wide ? 26 : 24, lineHeight: 1.25 }}>
          {item.label}
        </span>
        {item.note && (
          <span style={{ fontSize: 17, lineHeight: 1.3, color: style.inkDim }}>
            {item.note}
          </span>
        )}
      </span>
      {item.price && (
        <span
          style={{
            flex: "0 0 auto",
            fontSize: wide ? 24 : 20,
            fontWeight: 600,
            lineHeight: 1.3,
            color: style.accent,
          }}
        >
          {formatMoney(item.price, copy)}
        </span>
      )}
      {mode === "quantity" && (
        <span
          data-testid={`picker-step-${item.id}`}
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 12,
            minWidth: 130,
            justifyContent: "flex-end",
            fontSize: 24,
            lineHeight: 1.3,
            fontWeight: 700,
            color: picked ? style.ink : style.inkDim,
          }}
        >
          {/* The stepper is drawn only where it can be used: Left and Right
              belong to the FOCUSED line, and a minus sign on a line the guest
              is not standing on would be a control that does nothing. */}
          <span style={{ opacity: focused ? 1 : 0 }}>‹</span>
          <span data-testid={`picker-qty-${item.id}`}>
            {picked ? `${quantity} ×` : copy.quantityNone}
          </span>
          <span style={{ opacity: focused ? 1 : 0 }}>›</span>
        </span>
      )}
      {mode === "many" && picked && (
        <span
          aria-hidden
          style={{
            fontSize: 18,
            lineHeight: 1.3,
            fontWeight: 700,
            color: style.accent,
          }}
        >
          ✓
        </span>
      )}
    </button>
  );
}
